import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { planInfo, tierNameKey } from "./planCatalog";
import {
  parsePayModDate,
  planChangeErrorMessage,
  planChangeRefusal,
} from "../../services/planChange";

export const PLAN_CHANGE_I18N = "dashboard.billings.planChange";

export const usePlanChangeFormat = () => {
  const { t, i18n } = useTranslation();
  const k = useCallback(
    (key: string, options: Record<string, unknown> = {}) =>
      String(t(`${PLAN_CHANGE_I18N}.${key}`, options)),
    [t]
  );

  const money = useCallback(
    (cents: number | null | undefined, currency: string | null | undefined) => {
      if (cents === null || cents === undefined || !currency) return "";
      try {
        return new Intl.NumberFormat(i18n.language, {
          style: "currency",
          currency: currency.toUpperCase(),
        }).format(cents / 100);
      } catch {
        return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
      }
    },
    [i18n.language]
  );

  const count = useCallback(
    (value: number) => new Intl.NumberFormat(i18n.language).format(value),
    [i18n.language]
  );

  const date = useCallback((value: string | null | undefined) => {
    const parsed = parsePayModDate(value);
    return parsed.isValid() ? parsed.format("LL") : "";
  }, []);

  /** A refusal the dashboard knows is explained in its words; any other error keeps the server's message. */
  const failure = useCallback(
    (err: unknown) => {
      const reason = planChangeRefusal(err);
      if (reason) return k(`refusals.${reason}`);
      return planChangeErrorMessage(err) || k("errors.generic");
    },
    [k]
  );

  const tierName = useCallback(
    (code: string) => {
      const info = planInfo(code);
      return info ? (t(tierNameKey[info.tier]) as string) : k("anotherPlan");
    },
    [t, k]
  );

  const planName = useCallback(
    (code: string) => {
      const info = planInfo(code);
      if (!info) return k("anotherPlan");
      return k("planName", {
        tier: t(tierNameKey[info.tier]),
        interval: k(`interval.${info.interval}`),
      });
    },
    [t, k]
  );

  return { k, money, count, date, failure, tierName, planName };
};
